import {
  TableCellProps,
  WbCardContent,
  WbEntityRefLink,
  WbTable,
  WbWidget,
  customAlertApiRef,
} from '@agilelab/plugin-wb-platform';
import { HelpIcon } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Chip,
  Divider,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckCircle from '@material-ui/icons/CheckCircle';
import RadioButtonUnchecked from '@material-ui/icons/RadioButtonUnchecked';
import { Alert } from '@material-ui/lab';
import { useEffect } from 'react';
import { useMigrationContext } from '../../hooks/migration/useMigrationContext';
import { ResourceType } from '../../types';

const isRegistered = (type: ResourceType) => type.referencedBy !== undefined;

const detectedResourceTypeColumns = (theme: Theme) =>
  [
    {
      headerName: 'Status',
      field: 'status',
      width: 120,
      maxWidth: 120,
      minWidth: 120,
      resizable: false,
      fieldRender: e =>
        isRegistered(e) ? (
          <Tooltip title="Registered">
            <Chip
              label="Registered"
              variant="outlined"
              icon={
                <CheckCircle style={{ color: theme.palette.success.main }} />
              }
            />
          </Tooltip>
        ) : (
          <Tooltip title="Not registered">
            <RadioButtonUnchecked color="disabled" />
          </Tooltip>
        ),
    },
    {
      headerName: 'Name',
      field: 'name',
      fieldRender: e => (
        <Typography
          style={isRegistered(e) ? {} : { color: theme.palette.text.disabled }}
        >
          {e.name}
        </Typography>
      ),
    },
    {
      headerName: 'Kind',
      field: 'kind',
      fieldRender: e => (
        <Typography
          style={isRegistered(e) ? {} : { color: theme.palette.text.disabled }}
        >
          {e.kind === 'system' ? 'System type' : 'Component type'}
        </Typography>
      ),
    },
    {
      headerName: 'Referenced By',
      field: 'referencedBy',
      fieldRender: e => (
        <Typography
          style={isRegistered(e) ? {} : { color: theme.palette.text.disabled }}
        >
          {e.referencedBy ? (
            <WbEntityRefLink
              title={`${e.referencedBy.name} (${e.referencedBy.kind})`}
              entityRef={e.referencedBy}
            />
          ) : (
            '--'
          )}
        </Typography>
      ),
    },
  ] as TableCellProps<ResourceType>[];

export const DetectedResourceTypesWidget = () => {
  const { fetchResourceTypesState } = useMigrationContext();
  const theme = useTheme();
  const alertApi = useApi(customAlertApiRef);

  useEffect(() => {
    if (fetchResourceTypesState.error) {
      alertApi.post({
        message: `Error while fetching resource types: ${fetchResourceTypesState.error.message}`,
        severity: 'error',
      });
    }
  }, [fetchResourceTypesState, alertApi]);

  return (
    <WbWidget title="Detected resource types">
      <WbCardContent>
        <Alert
          severity="info"
          variant="outlined"
          icon={<HelpIcon />}
          style={{ border: 'none' }}
        >
          <Typography>
            The following resource types were detected by looking at the
            descriptors of existing systems and components. In the Practice
            Shaper, a resource type is considered registered when it is
            referenced in the <code>spec.resourceTypeId</code> property of a{' '}
            <code>SystemType</code> or <code>ComponentType</code>. Register the
            entities in the above panel to complete this step
          </Typography>
        </Alert>
        <Divider />
        <WbTable<ResourceType>
          components={{
            tableLoader: { loading: fetchResourceTypesState.loading },
            tableContent: {
              columns: detectedResourceTypeColumns(theme),
              rows:
                fetchResourceTypesState.value?.sort(
                  (a, b) => +isRegistered(a) - +isRegistered(b),
                ) ?? [],
            },
          }}
        />
      </WbCardContent>
    </WbWidget>
  );
};
